import _ from "lodash";
import { buildSelectField } from "./vueModelUtils";
import config from "../config";
import {
    Attribute,
    Experiment,
    Protocol,
    RelationOption,
    RelationOptions,
    RelationType,
    Thing,
    ThingSchema,
} from "@/services/things/models";
import {
    AbstractSchemaConfig,
    AbstractSchemaConfigWithGlobalRelationOptions,
} from "@/services/schemas/manageSchemasAPI";

export function getThingDisplay(thing: Thing): string {
    if (thing.id !== thing.displayName) {
        return `${thing.displayName} (ID ${thing.id})`;
    } else {
        return thing.displayName;
    }
}

export function findThingSchema(
    thingSchemaId: string,
    abstractSchemaConfig: AbstractSchemaConfig
): ThingSchema | undefined {
    return abstractSchemaConfig.thingSchemas.find(
        (thingSchema) => thingSchema.id === thingSchemaId
    );
}

export function isTopLevelSchema(thingSchema: ThingSchema): boolean {
    const parentThingSchemaName = thingSchema.parentThingSchemaName;
    return _.isNil(parentThingSchemaName) || parentThingSchemaName === "entry";
}

export function getParentThingSchema(
    thingSchema: ThingSchema,
    protocol: Protocol
): ThingSchema | null {
    if (isTopLevelSchema(thingSchema)) {
        return null;
    }
    return (
        protocol.thingSchemas.find((schema) => {
            return schema.name === thingSchema.parentThingSchemaName;
        }) || null
    );
}

export function getEntryRelationAttributes(attributesList: Attribute[]) {
    return getAttributesForType(attributesList, "entry_relation");
}

export function getGlobalRelationAttributes(attributesList: Attribute[]) {
    return getAttributesForType(attributesList, "global_relation");
}

export function getAttributesForType(attributesList: Attribute[], type: RelationType) {
    return attributesList.filter((attribute) => {
        return attribute.type === type;
    });
}

export function getAttributeNamesFromSchemaAttributeMap(schemaAttributeMap) {
    return Object.keys(schemaAttributeMap);
}

export function findParentSchema(thingSchema: ThingSchema, protocol: Protocol): ThingSchema | null {
    const parentName = thingSchema.parentThingSchemaName;
    if (parentName === "entry") {
        return null;
    }
    return protocol.thingSchemas.find((schema) => schema.name === parentName) || null;
}

export function isConditionAvailableOnThingsForSchema(
    thingSchema: ThingSchema,
    protocol: Protocol
) {
    const thingSchemaWithCondition = findSchemaWithConditionAttribute(thingSchema, protocol);
    return !_.isNil(thingSchemaWithCondition);
}

export function findConditionSchema(schemaConfig: AbstractSchemaConfig) {
    return schemaConfig.thingSchemas.find((thingSchema) => {
        return thingSchema.name === "experiment_conditions";
    });
}

export function findSchemaWithConditionAttribute(thingSchema: ThingSchema, protocol: Protocol) {
    let schema: ThingSchema | null = thingSchema;
    while (schema != null && !hasConditionAttribute(schema)) {
        schema = findParentSchema(schema, protocol);
    }

    return schema || null;
}

export function hasConditionAttribute(thingSchema: ThingSchema) {
    return !_.isNil(getAttribute(thingSchema, "condition"));
}

export function findConditionInExperimentById(experiment: Experiment, conditionId: string) {
    return experiment.conditions.find((condition) => {
        return condition.id === conditionId;
    });
}

export function requiredValue(v: unknown): true | string {
    return !!v || "Value is required";
}

export function numberValue(v: unknown): true | string {
    return !v || !isNaN(v as unknown as number) || "Value must be a number";
}

function getValidationRules(attribute: Attribute): AttributeFormFieldValidationRule[] {
    const rules: AttributeFormFieldValidationRule[] = [];
    if (attribute.required) {
        rules.push(requiredValue);
    }
    if (attribute.type === "number") {
        rules.push(numberValue);
    }
    return rules;
}

export function validateFormFields(formFields: AttributeFormField[]) {
    for (const field of formFields) {
        const rules = field.validationRules;
        if (rules && rules.length) {
            if (!rules.every((rule) => rule(field.value) === true)) {
                console.log("Validation failed for field", field.name, field, rules);
                return false;
            }
        }
    }

    return true;
}

export function isEditable(attribute: Attribute): boolean {
    return _.isNil(attribute.editable) || attribute.editable;
}

export type AttributeFormFieldValidationRule = (v: unknown) => true | string;

export type AttributeFormField = {
    label: string;
    name: string;
    attribute: Attribute;
    editable: boolean;
    value: unknown;
    validationRules: AttributeFormFieldValidationRule[];
};

export function getFormFieldConfigForThingSchemaAttribute(
    attribute: Attribute,
    value: unknown,
    protocol: AbstractSchemaConfigWithGlobalRelationOptions
): AttributeFormField {
    const editable = isEditable(attribute);
    const formValue = _.isNil(value) ? getDefaultValueForAttribute(attribute, protocol) : value;
    return {
        label: attribute.display_name!,
        name: attribute.name,
        attribute: attribute,
        editable: editable,
        value: formValue,
        validationRules: getValidationRules(attribute),
    };
}

function getDefaultValueForAttribute(
    attribute: Attribute,
    protocol: AbstractSchemaConfigWithGlobalRelationOptions
): unknown {
    if (_.isNil(attribute.subordinate_to_attribute) && !_.isNil(attribute.value_default)) {
        return attribute.value_default;
    }
    if (isGlobalRelationAttribute(attribute) && !_.isNil(attribute.option_default)) {
        // the default will be the label, needs to be translated to the value
        const options = getGlobalRelationOptionsForAttribute(attribute, protocol);
        const option = options.find((option) => option.displayName === attribute.option_default);
        return option ? option.value : null;
    }
    return attribute.option_default;
}

export function getThingSchemaById(
    protocol: AbstractSchemaConfig,
    thingSchemaId: string
): ThingSchema | undefined {
    return protocol.thingSchemas.find((thingSchema) => thingSchema.id === thingSchemaId);
}

export function getThingSchemaForId(
    abstractSchemaConfig: AbstractSchemaConfig,
    thingSchemaId: string
): ThingSchema | undefined {
    return abstractSchemaConfig.thingSchemas.find((schema) => {
        return schema.id === thingSchemaId;
    });
}

export function getAllAttributeForSchema(thingSchema: ThingSchema): Attribute[] {
    return [...getOneTimeAttributesList(thingSchema), ...getTimeSeriesAttributesList(thingSchema)];
}

export function getAttributesAsList(attributesMap: Record<string, Attribute>): Attribute[] {
    return Object.values(attributesMap);
}

export function getOneTimeAttributesList(thingSchema: ThingSchema): Attribute[] {
    return getAttributesAsList(thingSchema.oneTimeAttributes);
}

export function getTimeSeriesAttributesList(thingSchema: ThingSchema): Attribute[] {
    return getAttributesAsList(thingSchema.timeSeriesAttributes);
}

export function isTimeSeriesAttribute(thingSchema: ThingSchema, attribute: Attribute): boolean {
    const timeSeriesAttrs = getTimeSeriesAttributesList(thingSchema);
    const found = timeSeriesAttrs.find((attr) => attr.name === attribute.name);
    return !!found;
}

export function sortAttributesFn(a: Attribute, b: Attribute): number {
    if (a.display_order === b.display_order) {
        // the same or null, defer to the display name
        return a.display_name!.localeCompare(b.display_name!);
    } else if (_.isNil(a.display_order)) {
        return 1;
    } else if (_.isNil(b.display_order)) {
        return -1;
    }
    return a.display_order - b.display_order;
}

export function schemaContainsDateAttributes(thingSchema: ThingSchema): boolean {
    return getDateAttributes(thingSchema).length > 0;
}

export function isDateAttribute(attr: Attribute): boolean {
    return attr.type === "date" || attr.type === "datetime";
}

export function getDateAttributes(thingSchema: ThingSchema): Attribute[] {
    const attributes = getAllAttributeForSchema(thingSchema);
    return attributes.filter((attr) => {
        return isDateAttribute(attr);
    });
}

export function getDateAttributeOptions(thingSchema: ThingSchema): Option[] {
    const dateAttributes = getDateAttributes(thingSchema);
    return getAttributeOptionsForAttributesList(dateAttributes);
}

export function getEntryRelationAttributeOptions(
    attributesMap: Record<string, Attribute>
): Option[] {
    const attributesList = getAttributesAsList(attributesMap);
    const entryRelationAttributes = getEntryRelationAttributes(attributesList);
    return getAttributeOptionsForAttributesList(entryRelationAttributes);
}

export function getAllAttributeOptionsForSchema(thingSchema: ThingSchema): Option[] {
    if (!thingSchema) return [];

    const attributes = getAllAttributeForSchema(thingSchema);
    return getAttributeOptionsForAttributesList(attributes);
}

export type Option = {
    title: string;
    value: string;
};

export function getAttributeOptionsForAttributesList(attributesList: Attribute[]): Option[] {
    return attributesList.map((attribute) => {
        // Note: v-select requires `text` instead of `label`, so for consistency between v-select and
        // multi-select, use `text` as the label property.
        return {
            title: attribute.display_name!,
            value: attribute.name,
        };
    });
}

export function getAttributeOptions(attributesList: Attribute[]): Option[] {
    if (!attributesList) {
        return [];
    }
    return getAttributeOptionsForAttributesList(attributesList);
}

export function getAttribute(schema: ThingSchema, attributeName: string): Attribute | undefined {
    const attributes = getAllAttributeForSchema(schema);
    return attributes.find((attribute) => attribute.name === attributeName);
}

export function getThingSchemaOption(schema: ThingSchema): Option {
    return {
        title: schema.displayName,
        value: schema.id,
    };
}

export function getThingSchemaOptions(thingSchemas: ThingSchema[]): Option[] {
    return thingSchemas.map((schema) => {
        return getThingSchemaOption(schema);
    });
}

export type ThingSchemaSelectFieldParams = {
    label: string;
    initialThingSchema: ThingSchema | null;
    abstractSchemaConfig: AbstractSchemaConfig;
    changed: (thingSchema: ThingSchema | undefined) => void;
    schemaOptionFilterFn: (thingSchema: ThingSchema) => boolean;
};

export function buildThingSchemaSelectField({
    label,
    initialThingSchema,
    abstractSchemaConfig,
    changed,
    schemaOptionFilterFn,
}: ThingSchemaSelectFieldParams) {
    const validSchemaOptions = abstractSchemaConfig.thingSchemas.filter((schema) => {
        if (!schema.protocolCode && !schema.schemaConfigId) {
            return false;
        }
        return !schemaOptionFilterFn || schemaOptionFilterFn(schema);
    });

    const schemaOptions = getThingSchemaOptions(validSchemaOptions);

    return buildSelectField({
        name: label,
        options: schemaOptions,
        value: initialThingSchema ? initialThingSchema.id : null,
        changed: (value: string) => {
            const selectedThingSchema = getThingSchemaForId(abstractSchemaConfig, value);
            changed(selectedThingSchema);
        },
    });
}

export function isBasicDropdownWithOptions(attribute: Attribute): boolean {
    return (
        attribute.widget_type === "dropdown" &&
        !_.isNil(attribute.options) &&
        !isRelationAttribute(attribute)
    );
}

export function isNumericWithUnitsAttribute(attribute: Attribute): boolean {
    return attribute.widget_type === "numeric_w_units";
}

export function isArrayAttribute(attribute: Attribute): boolean {
    return attribute.type === "string_array" || attribute.type === "number_array";
}

export function isRelationAttribute(attribute: Attribute): boolean {
    return isGlobalRelationAttribute(attribute) || isEntryRelationAttribute(attribute);
}

export function isGlobalRelationAttribute(attribute: Attribute): boolean {
    return attribute.type === "global_relation";
}

export function isEntryRelationAttribute(attribute: Attribute): boolean {
    return attribute.type === "entry_relation";
}

export function getOptionsForForAttribute(
    attribute: Attribute,
    experiment: Experiment,
    protocol: AbstractSchemaConfigWithGlobalRelationOptions
) {
    if (attribute.type === "global_relation") {
        return getGlobalRelationOptionsForRelation(attribute.relation!, protocol);
    } else if (attribute.type === "entry_relation") {
        return getEntryRelationOptionsForRelation(attribute.relation!, experiment);
    } else {
        console.error("Attribute type not supported for relation options", attribute);
        return [];
    }
}

export function getEntryRelationOptionsForAttribute(
    attribute: Attribute,
    experiment: Experiment
): RelationOption[] {
    if (attribute.type === "entry_relation") {
        return getEntryRelationOptionsForRelation(attribute.relation!, experiment);
    } else {
        return [];
    }
}

export function getGlobalRelationOptionsForAttribute(
    attribute,
    protocol: AbstractSchemaConfigWithGlobalRelationOptions
) {
    if (attribute.type === "global_relation") {
        return getGlobalRelationOptionsForRelation(attribute.relation, protocol);
    } else {
        return [];
    }
}

export function getEntryRelationOptionsForRelation(
    relation: string,
    experiment: Experiment
): RelationOption[] {
    return getRelationOptionsAsList(experiment.entryRelationOptions, relation);
}

export function getGlobalRelationOptionsForRelation(
    relation: string,
    protocol: AbstractSchemaConfigWithGlobalRelationOptions
): RelationOption[] {
    return getRelationOptionsAsList(protocol.globalRelationOptions, relation);
}

export function getRelationOptionDisplayValue(
    relationAttribute,
    allRelationOptions: RelationOptions[],
    attributeValue
) {
    const relationOptions = getRelationOptionsAsList(
        allRelationOptions,
        relationAttribute.relation
    );

    return getDisplayValueForRelationOption(attributeValue, relationOptions);
}

function getRelationOptionsAsList(relationOptions: RelationOptions[], relation): RelationOption[] {
    const relationOptionsForRelation = relationOptions.find((relationOptions) => {
        return relationOptions.relation === relation;
    });

    if (!relationOptionsForRelation) {
        console.warn(`No relation options found for relation ${relation}`);
        return [];
    }

    return relationOptionsForRelation.options;
}

export function getDisplayValueForRelationOption(
    value: unknown,
    relationOptions: RelationOption[]
) {
    const option = relationOptions.find((option) => {
        return option.value === value;
    });
    return option ? option.displayName : null;
}

export function isFilterableAttribute(attribute: Attribute) {
    return (
        attribute.type !== "string_array" && !attribute.hidden && !attribute.name.endsWith("_units")
    );
}

export function getFilterableAttributes(attributeList: Attribute[]) {
    // remove unsupported data types and sort by display_order ascending
    return attributeList
        .filter((attribute) => isFilterableAttribute(attribute))
        .sort((a, b) => a.display_order! - b.display_order!);
}

export function downloadSummaryOneTimeMeasurementSetExcel({
    experimentCode,
    thingSchemaId,
}: {
    experimentCode: string;
    thingSchemaId: string;
}) {
    window.open(
        `${config.apiHost}/measurements/summaryOneTimeMeasurementSets/template/${experimentCode}/${thingSchemaId}/setup.xlsx`,
        "_blank"
    );
}

export function downloadCustomEntitySetupExcel({
    protocolCode,
    experimentCode,
    entityName,
    entityParent,
}: {
    protocolCode: string;
    experimentCode: string;
    entityName: string;
    entityParent: string;
}) {
    console.log("createCustomEntitySetupExcel:", experimentCode, entityName, entityParent);
    window.open(
        `${config.apiHost}/experiment/${experimentCode}/excel/customSetup/${protocolCode}/${entityName}/${entityParent}/${experimentCode}${entityName}Setup.xlsx`,
        "_blank"
    );
}
